import type { ArticleTag } from './types'

import downIcon from '@/assets/images/about_right.svg'
import SearchIcon from '@/assets/images/search.svg'

const SORT_LABELS: Record<string, string> = {
  asc: 'Oldest First',
  desc: 'Most Recent'
}

/**
 * The "Quick Filters" rail shared by the guides listing and each guide category
 * page. Markup, classNames and inline styles are copied verbatim from
 * `Guides.tsx` — `guides.css` keys off `checkbox-wrapper` /
 * `sort-checkbox-wrapper` / `guide-quick-filter-container`.
 *
 * Topic-checkbox wiring follows `Guides.tsx`: `onChange` sits on the `<input>`
 * with a unique id. `GuideCategory.tsx` had drifted to putting `onChange` on
 * the `<label>` around an `<input checked>` with no handler and a hardcoded
 * `id="item1"` — React treats that input as read-only and warns, and the toggle
 * only worked because the change event bubbles. The rendered DOM and the user's
 * toggling behaviour are the same either way; this drops the warning and the
 * duplicate ids.
 */
export function QuickFilterSidebar({
  articleTags,
  search,
  setSearch,
  setSelectedSort,
  selectedTopics,
  setSelectedTopics,
  sortActive,
  setSortActive,
  topicActive,
  setTopicActive
}: {
  articleTags: ArticleTag[]
  search: string
  setSearch: (v: string) => void
  setSelectedSort: (v: string) => void
  selectedTopics: string[]
  setSelectedTopics: (updater: (prev: string[]) => string[]) => void
  sortActive: boolean
  setSortActive: (v: boolean) => void
  topicActive: boolean
  setTopicActive: (v: boolean) => void
}) {
  return (
    <>
      <div style={{ color: '#000' }}>
        <h6>Quick Filters</h6>
        <div
          className="input-group input-group-lg my-3 rounded-3 overflow-hidden"
          style={{
            border: '1px solid var(--border-grey)'
          }}
        >
          <input
            type="text"
            className="form-control border-0"
            placeholder="Search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ fontSize: '16px' }}
          />
          <div
            className="input-group-text border-0"
            style={{ backgroundColor: 'white', height: '48px' }}
          >
            <img src={SearchIcon.src} alt="" style={{ margin: '2.5px' }} />
          </div>
        </div>

        <div>
          <div
            className="d-flex justify-content-between align-items-center p-2 mt-2"
            style={{
              borderBottom: '1px solid var(--border-grey)',
              cursor: 'pointer'
            }}
            onClick={() => {
              setSortActive(false)
              setTopicActive(!topicActive)
            }}
          >
            <p className="m-0">Topics </p>
            <span>
              <img
                src={downIcon.src}
                alt=""
                style={{
                  rotate: topicActive ? '270deg' : '90deg',
                  transition: 'all 0.3s ease-in'
                }}
              />
            </span>
          </div>
          <ul
            style={{
              height: topicActive ? '180px' : '0',
              transition: 'all 0.3s ease-in',
              listStyle: 'none',
              padding: 0,
              maxHeight: '180px',
              overflow: 'scroll',
              scrollbarWidth: 'none'
            }}
          >
            {articleTags &&
              articleTags?.map((tag, tagIndex) => {
                const isChecked = selectedTopics.includes(tag.slug ?? '')
                return (
                  <li
                    key={tag.slug ?? `topic-${tagIndex}`}
                    style={{
                      padding: '15px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px'
                    }}
                  >
                    <label className="checkbox-wrapper">
                      <input
                        type="checkbox"
                        id={`topic-${tag.slug}`}
                        checked={isChecked}
                        onChange={() => {
                          if (isChecked) {
                            setSelectedTopics(prev => prev.filter(slug => slug !== tag.slug))
                          } else {
                            setSelectedTopics(prev => [...prev, tag.slug ?? ''])
                          }
                        }}
                      />
                      <span className="custom-checkbox"></span>
                      {tag?.title}
                    </label>
                  </li>
                )
              })}
          </ul>
          <div
            className="d-flex justify-content-between align-items-center p-2 mt-2"
            style={{
              borderBottom: '1px solid var(--border-grey)',
              cursor: 'pointer'
            }}
            onClick={() => {
              setSortActive(!sortActive)
              setTopicActive(false)
            }}
          >
            <p className="m-0">Sort by </p>
            <span>
              <img
                src={downIcon.src}
                alt=""
                style={{
                  rotate: sortActive ? '270deg' : '90deg',
                  transition: 'all 0.3s ease-in'
                }}
              />
            </span>
          </div>
          <ul
            style={{
              height: sortActive ? '102px' : '0',
              overflow: 'hidden',
              transition: 'all 0.3s ease-in',
              listStyle: 'none',
              padding: 0,
              margin: 0
            }}
          >
            {['desc', 'asc'].map(type => (
              <li
                key={type}
                style={{
                  padding: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                <label className="sort-checkbox-wrapper" onClick={() => setSelectedSort(type)}>
                  {SORT_LABELS[type]}
                </label>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <span
        style={{
          color: 'var(--alrajhi-primary)',
          fontSize: '14px',
          fontWeight: '500',
          padding: '0 10px',
          cursor: 'pointer'
        }}
        onClick={() => {
          setSelectedSort('')
          setSelectedTopics(() => [])
          setSearch('')
        }}
      >
        Clear all filters
      </span>
    </>
  )
}
